import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { submitForm, isValidEmail } from '../../../services/formsService';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitStatus(null);
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitForm('contact', formData);

      if (result?.success) {
        setSubmitStatus('success');
        setSubmitMessage(result?.message);
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          company: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result?.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Status Messages */}
      {submitStatus === 'success' && (
        <div className="flex items-start gap-3 p-4 rounded-md" style={{ background: 'rgba(79, 179, 184, 0.1)', border: '1px solid rgba(79, 179, 184, 0.2)' }}>
          <Icon name="CheckCircle" size={20} style={{ color: 'var(--color-success)' }} className="mt-0.5" />
          <p className="text-sm" style={{ color: 'var(--color-success)' }}>{submitMessage}</p>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="flex items-start gap-3 p-4 rounded-md" style={{ background: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.2)' }}>
          <Icon name="AlertCircle" size={20} style={{ color: 'var(--color-destructive)' }} className="mt-0.5" />
          <p className="text-sm" style={{ color: 'var(--color-destructive)' }}>{submitMessage}</p>
        </div>
      )}
      {/* Form Fields */}
      <Input
        label="Full Name"
        name="fullName"
        value={formData?.fullName}
        onChange={handleChange}
        error={errors?.fullName}
        required
        placeholder="John Doe"
        disabled={isSubmitting}
      />
      <Input
        label="Email"
        name="email"
        type="email"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        placeholder="john@company.com"
        disabled={isSubmitting}
      />
      <Input
        label="Company"
        name="company"
        value={formData?.company}
        onChange={handleChange}
        placeholder="Your Company (optional)"
        disabled={isSubmitting}
      />
      <div className="space-y-2">
        <label
          htmlFor="message"
          className="text-sm font-medium leading-none"
          style={{ color: 'var(--color-foreground)', fontFamily: 'IBM Plex Sans' }}
        >
          Message
          <span className="ml-1" style={{ color: 'var(--color-destructive)' }}>*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData?.message}
          onChange={handleChange}
          rows={5}
          className="flex w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          style={{ 
            background: 'var(--color-card)', 
            borderColor: 'var(--color-input)', 
            color: 'var(--color-foreground)',
            fontFamily: 'IBM Plex Sans'
          }}
          placeholder="Tell us how we can help..."
          disabled={isSubmitting}
        />
        {errors?.message && (
          <p className="text-sm" style={{ color: 'var(--color-destructive)' }}>{errors?.message}</p>
        )}
      </div>
      <Button
        type="submit"
        fullWidth
        loading={isSubmitting}
        disabled={isSubmitting}
        iconName="Send"
        iconPosition="right"
      >
        {isSubmitting ? 'Sending...' : 'Send message'}
      </Button>
    </form>
  );
};

export default ContactForm;